import { authFetch } from './client';
// Quality Check — scheduled regression runs of project pipelines. A registered
// entry re-runs a workflow (weekly/monthly or on demand) in a throwaway project
// and diffs its numeric outputs against a stored baseline. Backed by
// /api/quality-checks; see product_backend/app/services/quality_check/CONTEXT.md.

// authFetch throws Error(detail || 'Request failed: {status}') — it doesn't carry
// the status code, so tolerance below matches on the message (same as
// src/api/tutorialRun.js).
const isNotFound = (err) => /404|not found/i.test(err?.message || '');

// GET the workflow groups in a project that can be registered. Each candidate
// carries `runnable` + a plain-language `reason` when it can't be — the backend
// never guesses a template, so an untagged workflow is refused explicitly.
export const listQualityCheckCandidates = async (projectId) => {
  const data = await authFetch(
    `/api/quality-checks/candidates?projectId=${encodeURIComponent(projectId)}`
  );
  return data.candidates || [];
};

// GET the caller's suite. Each entry carries its latest run summary.
export const listQualityChecks = async () => {
  const data = await authFetch('/api/quality-checks/entries');
  return data.entries || [];
};

// POST register a workflow. Captures the graph snapshot immediately, so later
// edits to the source project do not change what this entry runs.
export const createQualityCheck = async ({
  projectId,
  groupId,
  name,
  description,
  interval,
  solverComputeType,
  runHourUtc,
  startAt,
  overrides,
  runMode,
}) => {
  const body = { projectId, groupId };
  if (name) body.name = name;
  if (description) body.description = description;
  if (interval) body.interval = interval;
  if (solverComputeType) body.solverComputeType = solverComputeType;
  if (runHourUtc !== undefined) body.runHourUtc = runHourUtc;
  // The recurrence anchor, as ISO UTC. Omitted (not null) when the user didn't
  // pick one, so the backend keeps its runHourUtc default.
  if (startAt) body.startAt = startAt;
  if (overrides) body.overrides = overrides;
  // 'state' (default — replay the frozen module settings) or 'interaction'
  // (replay the recorded session: the prompts in order, the agent's answers, and
  // the manual changes between them). The backend refuses `interaction` when the
  // recording cannot rebuild the workflow.
  if (runMode) body.runMode = runMode;
  return authFetch('/api/quality-checks/entries', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

// PATCH name / description / interval / enabled / runHourUtc / startAt /
// tolerances / overrides. NOTE `startAt: ''` CLEARS the anchor while omitting the
// key leaves it alone — the backend cannot distinguish "unset" from "clear"
// otherwise.
export const updateQualityCheck = (entryId, updates) =>
  authFetch(`/api/quality-checks/entries/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });

// DELETE the entry, its run history, and every run project's artifacts.
export const deleteQualityCheck = (entryId) =>
  authFetch(`/api/quality-checks/entries/${entryId}`, { method: 'DELETE' });

// POST run now. 409s if a run is already in flight (the same claim the scheduled
// sweep uses), so a double-click can't launch twice. When the concurrency cap has
// no room the entry is QUEUED instead — the response carries
// `status: 'running' | 'queued'`.
export const runQualityCheckNow = (entryId) =>
  authFetch(`/api/quality-checks/entries/${entryId}/run`, { method: 'POST' });

// POST run several now — starts what fits and queues the rest. ONE request rather
// than a client-side fan-out, so the backend reads the concurrency budget once and
// the checks fill it in the order they were selected. Returns
// `{results: [{entryId, status}], started, queued}`.
export const runQualityChecks = (entryIds) =>
  authFetch('/api/quality-checks/entries/run', {
    method: 'POST',
    body: JSON.stringify({ entryIds }),
  });

// POST cancel the in-flight run. Returns null when there is none (404).
export const cancelQualityCheckRun = async (entryId) => {
  try {
    return await authFetch(`/api/quality-checks/entries/${entryId}/cancel`, {
      method: 'POST',
    });
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
};

// POST accept a run's metrics as the new baseline (defaults to the latest run
// that recorded metrics). Used after an INTENTIONAL numeric change, otherwise
// the check stays red forever.
export const acceptQualityCheckBaseline = (entryId, runId) => {
  const qs = runId ? `?runId=${encodeURIComponent(runId)}` : '';
  return authFetch(`/api/quality-checks/entries/${entryId}/baseline${qs}`, {
    method: 'POST',
  });
};

// GET run history (newest first). The newest run also carries live per-step
// `progress` — a run project is hidden from GET /api/projects, so this is the
// only channel for its step statuses.
export const listQualityCheckRuns = async (entryId, limit) => {
  const qs = limit ? `?limit=${limit}` : '';
  const data = await authFetch(`/api/quality-checks/entries/${entryId}/runs${qs}`);
  return data.runs || [];
};
